import React, { useState } from "react";
import { Card, Jumbotron, Container, Row, Col } from "react-bootstrap";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Clipboard } from "react-feather";
import yaml from "js-yaml";
import { encryptValue, getSealedSecret } from "@socialgouv/aes-gcm-rsa-oaep";
import { Form as BsForm, Button } from "react-bootstrap";

import "bootstrap/dist/css/bootstrap.min.css";

import { isValidKey } from "./isValidKey";
import { Form } from "./Form";
import { Protip } from "./Protip";
import { useMemo } from "react";

const Intro = () => (
  <Jumbotron
    style={{ marginTop: "1rem", marginBottom: "1rem", padding: "1rem 1rem" }}
  >
    <h1>WebSeal</h1>
    <p>Client-side sealed-secrets generation</p>
  </Jumbotron>
);

const CodeArea = (props) => (
  <textarea
    {...props}
    style={{
      fontSize: "0.8rem",
      fontFamily: "Courier",
      border: "1px solid #ccc",
      width: "100%",
      padding: 5,
      height: 400,
      ...(props.style || {}),
    }}
  ></textarea>
);

const RadioChoice = React.forwardRef(({ name, value, ...props }, ref) => (
  <BsForm.Check
    inline
    ref={ref}
    name={name}
    id={`${name}-${value}`}
    label={value}
    type="radio"
    value={value}
    {...props}
  />
));

const Copier = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <CopyToClipboard
      text={text}
      onCopy={() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
    >
      <Clipboard
        style={{
          marginLeft: 10,
          cursor: "pointer",
          transition: "all 0.2s ease-in",
        }}
        color={copied ? "green" : "black"}
        title="Copy"
        size={16}
      />
    </CopyToClipboard>
  );
};

const Editor = () => {
  const [formData, setFormData] = useState({
    cluster: "dev",
    namespace: "",
    name: "",
    pemKey: "",
  });
  const [encrypted, setEncrypted] = useState(null);
  const [jsonResult, setJsonResult] = useState(null);
  const [yamlResult, setYamlResult] = useState(null);

  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [git_url, setGitUrl] = useState("")
  const [env, setEnv] = useState("production")

  const suggested_namespace = useMemo(() => {
    const base = git_url ? git_url.replace("https://gitlab.com/ts-workflow/", "").replace("/", "-") : formData.namespace
    if (env == "other") return base;
    return `${base}-${env}`
  }, [git_url, formData.namespace, env])
  const onSubmit = async (data) => {
    //console.log("onSubmit2", data);
    const validKey = isValidKey(data.pemKey);
    setFormData(data);
    setEncrypted("");
    setYamlResult("");
    if (!validKey) {
      return;
    }
    if (data.value && data.value !== formData.value) {
      const pemKey = data.pemKey;
      const values = {};
      if (data.value.match(/^([\w_-\d]+)=(.+)$/im)) {
        data.value.split("\n").forEach((row) => {
          const matches = row.match(/^([\w_-\d]+)=(.*)$/i);
          if (matches) {
            values[matches[1]] = matches[2];
          }
        });
      } else {
        values.VALUE = data.value;
      }
      let sealedSecret;
      try {
        sealedSecret = await getSealedSecret({
          pemKey,
          namespace: data.namespace || "some-namespace",
          name: data.name || "some-secret-name",
          scope: data.scope,
          values,
        });
      } catch (e) {
        console.log("cannot create sealed secret", e.message);
        return;
      }
      if (data.scope === "strict" && (!data.namespace || !data.name)) {
        console.log("namespace and name are mandatory");
        setYamlResult("");
        setEncrypted("");
      } else if (data.scope === "namespace" && !data.namespace) {
        console.log("namespace is mandatory");
        setYamlResult("");
        setEncrypted("");
      } else if (!data.value) {
        console.log("value is mandatory");
        setYamlResult("");
        setEncrypted("");
      } else {
        const keys = Object.keys(values);
        if (keys.length === 1) {
          setEncrypted(sealedSecret.spec.encryptedData[keys[0]]);
        } else {
          setEncrypted(
            "Not available for multiple values, use the below secret"
          );
        }
        setJsonResult(sealedSecret);
        setYamlResult(yaml.dump(sealedSecret, { noRefs: true, lineWidth: -1 }));
      }
    }
  };
  return (
    <Container>
      <Intro />
      <Row>
        <Col xs={12}>
          <Form onSubmit={onSubmit} initialFormData={formData} />

          {encrypted && (
            <>

              <Card style={{ marginTop: 10 }}>
                <Card.Body>
                  <Card.Title>
                    SealedSecret <Copier text={yamlResult} />
                  </Card.Title>
                  <CodeArea defaultValue={yamlResult} />
                </Card.Body>
              </Card>
              <Card style={{ marginTop: 10 }}>
                <Card.Body>
                  <BsForm.Group>
                    <h4>Git magic :</h4>
                    <BsForm.Control
                      name="git_url"
                      required
                      type="text"
                      placeholder="Git url"
                      onChange={(e) => setGitUrl(e.target.value)}
                    />
                    <div style={{ display: "flex", gap: 8, marginTop: 8, marginBottom: 8 }}>
                      <BsForm.Control
                        name="username"
                        required
                        type="text"
                        placeholder="Username"
                        onChange={(e) => setUsername(e.target.value)}
                      />
                      <BsForm.Control
                        name="password"
                        required
                        type="text"
                        placeholder="Password"
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <Button onClick={() => {
                        localStorage.git_password = password
                        localStorage.git_username = username
                        setPassword(password)
                      }}>Save</Button>
                    </div>
                    {localStorage.git_password && <div>
                      Using saved credentials
                    </div>}
                    {suggested_namespace != formData.namespace && <div style={{ color: "red" }}>
                      Use namespace : {suggested_namespace}
                    </div>}
                    {env == "production" && <div style={{ color: "red" }}>
                      Check if scope is "namespace"
                    </div>}

                    <Row>
                      <Col xs={2}>
                        <BsForm.Label style={{ marginRight: 10 }}>Environment :</BsForm.Label>
                      </Col>
                      <Col xs={10}>
                        <RadioChoice
                          name="scope"
                          value="production"
                          onChange={(e) => {
                            setEnv(e.target.value)
                          }}
                        />
                        <RadioChoice
                          name="scope"
                          value="staging"
                          onChange={(e) => {
                            setEnv(e.target.value)
                          }}
                        />
                        <RadioChoice
                          name="scope"
                          value="other"
                          onChange={(e) => {
                            setEnv(e.target.value)
                          }}
                        />
                      </Col>
                    </Row>
                    <Button onClick={() => {
                      const result = {
                        metadata: {
                          name: jsonResult.metadata.name
                        },
                        ...jsonResult
                      }
                      let content = JSON.stringify(result);
                      if (env == "other") {
                        content = `if (std.extVar("env_slug") != "production" && std.extVar("env_slug") != "staging") then ${content}`
                      } else {
                        content = `if std.extVar("env_slug") == "${env}" then ${content}`
                      }
                      fetch("https://devops-pipeline-manager-production.trendsurfers.ru/secrets", {
                        body: JSON.stringify({
                          "git_url": git_url,
                          "git_username": localStorage.git_username,
                          "git_password": localStorage.git_password,
                          "files": {
                            [`${formData.name}.${env}.jsonnet`]: content,
                          },
                          "names": [
                            formData.name
                          ]
                        }),
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json"
                        }
                      })
                    }}>Upload</Button>
                  </BsForm.Group>
                </Card.Body>
              </Card>
              <Protip />
              <br />
              <br />
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default function App() {
  return (
    <div className="App">
      <Editor />
    </div>
  );
}
